import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, UserPlus } from 'lucide-react';
import { FriendAccessLevel, ACCESS_LEVEL_LABELS, ACCESS_LEVEL_DESCRIPTIONS } from '../types/friends';
import { motion, AnimatePresence } from 'framer-motion';

interface FriendRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (emailOrUsername: string, accessLevel: FriendAccessLevel) => Promise<void>;
}

const FriendRequestModal = ({ isOpen, onClose, onSend }: FriendRequestModalProps) => {
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [accessLevel, setAccessLevel] = useState<FriendAccessLevel>('restricted-access');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!emailOrUsername.trim()) {
      setError('Email or username is required');
      return;
    }

    setIsLoading(true);
    try {
      await onSend(emailOrUsername.trim(), accessLevel);
      setEmailOrUsername('');
      setAccessLevel('restricted-access');
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to send friend request');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-teal-600" />
                  Add Friend
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div>
                  <label htmlFor="emailOrUsername" className="block text-sm font-medium text-gray-700 mb-2">
                    Friend&apos;s email or @username
                  </label>
                  <input
                    id="emailOrUsername"
                    type="text"
                    value={emailOrUsername}
                    onChange={(e) => setEmailOrUsername(e.target.value)}
                    placeholder="friend@example.com or @their_username"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Access Level to Give Your Friend
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Select what level of access you want to give this friend to your cruise calendar.
                    They will choose their own access level for you when they accept.
                    <span className="block mt-1 text-gray-400 italic">
                      This is your initial access level and can be modified later from the Friends page.
                    </span>
                  </p>
                  <div className="space-y-3">
                    {(['full-access', 'restricted-access'] as FriendAccessLevel[]).map((level) => (
                      <label
                        key={level}
                        className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          accessLevel === level
                            ? 'border-secondary-500 bg-secondary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="accessLevel"
                          value={level}
                          checked={accessLevel === level}
                          onChange={(e) =>
                            setAccessLevel(e.target.value as FriendAccessLevel)
                          }
                          className="mt-1 mr-3"
                          disabled={isLoading}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900">
                              {ACCESS_LEVEL_LABELS[level]}
                            </span>
                            {level === 'full-access' && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                Recommended
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {ACCESS_LEVEL_DESCRIPTIONS[level]}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Send Request'}
                  </button>
                </div>
              </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default FriendRequestModal;

